import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../services/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);

  const updateUserMetadata = async (session) => {
    if (!session?.user) return null;

    try {
      console.log('🔄 Actualizando metadata del usuario...');
      
      // Si el usuario se autenticó con Google y no tiene avatar_url
      if (
        session.user.app_metadata?.provider === 'google' && 
        !session.user.user_metadata?.avatar_url &&
        session.user.identities?.[0]?.identity_data
      ) {
        const { picture, name, email } = session.user.identities[0].identity_data;
        
        console.log('📸 Encontrada foto de Google:', picture);
        
        // Actualizar metadata con la foto de Google
        const { data: { user: updatedUser }, error } = await supabase.auth.updateUser({
          data: { 
            avatar_url: picture,
            name: name,
            email: email
          }
        });

        if (error) throw error;
        console.log('✅ Metadata actualizada con foto de Google');
        return updatedUser;
      }
      
      return session.user;
    } catch (err) {
      console.error('Error actualizando metadata:', err);
      return session.user;
    }
  };

  useEffect(() => {
    // Verificar sesión actual
    const checkSession = async () => {
      try {
        console.log('🔍 Verificando sesión actual...');
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('❌ Error al obtener sesión:', error);
          throw error;
        }
        
        console.log('✅ Sesión obtenida:', session ? {
          id: session.user.id,
          email: session.user.email,
          provider: session.user.app_metadata?.provider,
          aud: session.user.aud
        } : 'No hay sesión');

        if (session) {
          const updatedUser = await updateUserMetadata(session);
          setUser(updatedUser);
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Error al verificar sesión:', err);
        setError(err.message);
      } finally {
        setLoading(false);
        setSessionChecked(true);
      }
    };

    checkSession();

    // Suscribirse a cambios en la autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth state changed:', event);
      console.log('📦 Nueva sesión:', session ? {
        id: session.user.id,
        email: session.user.email,
        provider: session.user.app_metadata?.provider,
        aud: session.user.aud
      } : 'No hay sesión');
      
      if (session) {
        const updatedUser = await updateUserMetadata(session);
        setUser(updatedUser);
      } else {
        setUser(null);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async () => {
    try {
      setError(null);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) throw error;
    } catch (err) {
      console.error('Error en login:', err);
      setError(err.message);
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (err) {
      console.error('Error en logout:', err);
      setError(err.message);
    }
  };

  const value = {
    user,
    loading,
    error,
    signIn,
    signOut,
    sessionChecked
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};